import type {GenderCode} from '@common/gender';
import type {RouterInput, RouterOutput} from '@common-web/lib/trpc';

export type VipRecord = RouterOutput['vip']['get'];
export type VipType = NonNullable<VipRecord['vipType']>;
export type VipFormMode = 'create' | 'edit';

export type VipCreateDetails = RouterInput['vip']['create']['memberDetails'];
export type VipUpdatePatch = RouterInput['vip']['update']['patch'];

export type VipFormValues = {
  address1: string;
  address2: string;
  city: string;
  dateOfBirth: string;
  email: string;
  firstName: string;
  gender: '' | GenderCode;
  lastName: string;
  phoneNumber: string;
  state: string;
  vipBrokerage: string;
  vipType: '' | VipType;
  zipCode: string;
};

export type VipFormRecord = Pick<
  VipRecord,
  | 'address1'
  | 'address2'
  | 'city'
  | 'dateOfBirth'
  | 'email'
  | 'firstName'
  | 'gender'
  | 'lastName'
  | 'phoneNumber'
  | 'state'
  | 'vipBrokerage'
  | 'vipType'
  | 'zipCode'
>;

export const VIP_TYPE_LABELS: Record<VipType, string> = {
  broker: 'Broker',
  other: 'Other',
  owner: 'Owner',
  team: 'Team',
};

export const EMPTY_VIP_FORM_VALUES: VipFormValues = {
  address1: '',
  address2: '',
  city: '',
  dateOfBirth: '',
  email: '',
  firstName: '',
  gender: '',
  lastName: '',
  phoneNumber: '',
  state: '',
  vipBrokerage: '',
  vipType: '',
  zipCode: '',
};

const stripNonDigits = (value: string) => value.replace(/\D/g, '');

export function vipRecordToFormValues({
  address1,
  address2,
  city,
  dateOfBirth,
  email,
  firstName,
  gender,
  lastName,
  phoneNumber,
  state,
  vipBrokerage,
  vipType,
  zipCode,
}: VipFormRecord): VipFormValues {
  return {
    address1: address1 ?? '',
    address2: address2 ?? '',
    city: city ?? '',
    dateOfBirth: dateOfBirth ?? '',
    email: email ?? '',
    firstName,
    gender: gender ?? '',
    lastName,
    phoneNumber: phoneNumber ?? '',
    state: state ?? '',
    vipBrokerage: vipBrokerage ?? '',
    vipType: vipType ?? '',
    zipCode: zipCode ?? '',
  };
}

/** Email is optional, but when present it must be a plausible address. */
export function isVipEmailValid(email: string): boolean {
  const trimmed = email.trim();
  return !trimmed || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
}

/** Zip codes are exactly 5 digits. */
export function isVipZipValid(zipCode: string): boolean {
  return /^\d{5}$/.test(zipCode);
}

/** Phone numbers must have exactly 10 digits once formatting is stripped. */
export function isVipPhoneValid(phoneNumber: string): boolean {
  return /^\d{10}$/.test(stripNonDigits(phoneNumber));
}

/**
 * Create requires every field. Edit requires name and gender (and VIP type and brokerage where
 * used) and checks state, zip, email and phone only once changed, so a bad stored value doesn't
 * block saving. A stored phone can't be cleared: create requires one.
 */
export function isVipFormValid(
  values: VipFormValues,
  options:
    | {mode: 'create'; usesVipType: boolean}
    | {initial: VipFormValues; mode: 'edit'; usesVipType: boolean},
): boolean {
  const {
    address1,
    city,
    dateOfBirth,
    email,
    firstName,
    gender,
    lastName,
    phoneNumber,
    state,
    vipBrokerage,
    vipType,
    zipCode,
  } = values;
  const {usesVipType} = options;
  const baseValid =
    !!firstName.trim() &&
    !!lastName.trim() &&
    !!gender &&
    (!usesVipType || (!!vipType && !!vipBrokerage.trim()));
  if (!baseValid) return false;

  if (options.mode === 'edit') {
    const {initial} = options;
    return (
      (state === initial.state || !!state) &&
      (zipCode === initial.zipCode || isVipZipValid(zipCode)) &&
      (phoneNumber === initial.phoneNumber || isVipPhoneValid(phoneNumber)) &&
      (email === initial.email || isVipEmailValid(email))
    );
  }

  return (
    !!state &&
    isVipZipValid(zipCode) &&
    isVipPhoneValid(phoneNumber) &&
    !!address1.trim() &&
    !!city.trim() &&
    !!dateOfBirth &&
    isVipEmailValid(email)
  );
}

/** Normalizes create-form values; null when a required enum selection is missing. */
export function buildVipCreateDetails(
  values: VipFormValues,
  usesVipType: boolean,
): VipCreateDetails | null {
  const {
    address1,
    address2,
    city,
    dateOfBirth,
    email,
    firstName,
    gender,
    lastName,
    phoneNumber,
    state,
    vipBrokerage,
    vipType,
    zipCode,
  } = values;
  if (!gender) return null;
  if (usesVipType && !vipType) return null;
  return {
    address1: address1.trim(),
    address2: address2.trim(),
    city: city.trim(),
    dateOfBirth,
    email: email.trim(),
    firstName: firstName.trim(),
    gender,
    lastName: lastName.trim(),
    phoneNumber: stripNonDigits(phoneNumber),
    state,
    zipCode: stripNonDigits(zipCode),
    ...(usesVipType && vipType ? {vipBrokerage: vipBrokerage.trim(), vipType} : {}),
  };
}

/**
 * Normalizes only the fields that differ from `initial` into an update patch; null when a required
 * enum selection is missing.
 */
export function buildVipUpdatePatch(
  values: VipFormValues,
  initial: VipFormValues,
  usesVipType: boolean,
): VipUpdatePatch | null {
  const {
    address1,
    address2,
    city,
    dateOfBirth,
    email,
    firstName,
    gender,
    lastName,
    phoneNumber,
    state,
    vipBrokerage,
    vipType,
    zipCode,
  } = values;
  if (!gender) return null;
  if (usesVipType && !vipType) return null;
  const changed = (key: keyof VipFormValues) => values[key] !== initial[key];
  return {
    ...(changed('address1') ? {address1: address1.trim()} : {}),
    ...(changed('address2') ? {address2: address2.trim()} : {}),
    ...(changed('city') ? {city: city.trim()} : {}),
    // DOB can't be cleared, only changed; skip the patch field when the input is empty.
    ...(changed('dateOfBirth') && dateOfBirth ? {dateOfBirth} : {}),
    ...(changed('email') ? {email: email.trim()} : {}),
    ...(changed('firstName') ? {firstName: firstName.trim()} : {}),
    ...(changed('gender') ? {gender} : {}),
    ...(changed('lastName') ? {lastName: lastName.trim()} : {}),
    ...(changed('phoneNumber') ? {phoneNumber: stripNonDigits(phoneNumber)} : {}),
    ...(changed('state') ? {state} : {}),
    ...(usesVipType && changed('vipBrokerage') ? {vipBrokerage: vipBrokerage.trim()} : {}),
    ...(usesVipType && vipType && changed('vipType') ? {vipType} : {}),
    ...(changed('zipCode') ? {zipCode: stripNonDigits(zipCode)} : {}),
  };
}
